import { useMemo } from 'react'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'
import { skipToken } from '@reduxjs/toolkit/query'

import { Consultation } from '@/core/models/consultation.model'
import { FormFieldType, FormSubmissionPurpose, FormSubmissionStatus } from '@/core/models/form.model'
import { ReadinessChecklistStatus, WaitingRoom } from '@/core/models/waiting-room.model'
import {
  useCreateFormSubmissionMutation,
  useGetConsultationFormSubmissionsQuery,
  useGetFormTemplateQuery,
  useUpdateFormSubmissionMutation,
} from '@/core/services/form'
import { getApiErrorMessage } from '@/utils/errors'

/** A response counts as answered for any field type (non-empty string/array, true boolean, any number/date). */
const isAnswered = (value: any): boolean => {
  if (value === null || value === undefined) return false
  if (typeof value === 'string') return value.trim().length > 0
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'boolean') return value
  return true
}

export type ReadinessChecklistState = {
  enabled: boolean
  templateTitle: string
  // Boolean inline path (unchanged): the doctor/operator tick checkboxes in a popover.
  items: Array<{ fieldId: string; label: string; done: boolean }>
  completed: number
  total: number
  isSaving: boolean
  toggleItem: (fieldId: string, done: boolean) => Promise<void>
  // Generalised path: any field type, filled via the dedicated form-submission page and submitted.
  hasNonBooleanFields: boolean
  status: ReadinessChecklistStatus
  isComplete: boolean // boolean: all ticked; non-boolean: form submitted
  uncheckedCount: number // for the soft-confirm copy
  openPreparation: () => void // opens the full form (non-boolean templates)
}

/**
 * Operator-side waiting-room preparation checklist. The definition is the room's readiness
 * FormTemplate. Boolean-only templates keep the inline tick popover (auto-saved draft). Templates
 * with any non-boolean field are filled via the dedicated form-submission page; submitting it
 * (draft → submitted) is the "prepared" signal the doctor sees.
 */
export function useReadinessChecklist({
  consultation,
  waitingRoom,
}: {
  consultation?: Consultation | null
  waitingRoom?: WaitingRoom | null
}): ReadinessChecklistState {
  const { t } = useTranslation()
  const { enqueueSnackbar } = useSnackbar()

  const readinessEnabled = waitingRoom?.readinessConfig?.enabled ?? false
  const templateId = readinessEnabled ? waitingRoom?.readinessConfig?.checklistTemplateId : undefined
  const consultationId = consultation?._id

  const { data: template } = useGetFormTemplateQuery(templateId ?? skipToken)
  const { data: submissionsData } = useGetConsultationFormSubmissionsQuery(
    templateId && consultationId ? { consultationId, limit: 100, skip: 0, order: 'DESC' } : skipToken
  )

  const [createFormSubmission, { isLoading: isCreating }] = useCreateFormSubmissionMutation()
  const [updateFormSubmission, { isLoading: isUpdating }] = useUpdateFormSubmissionMutation()

  const fields = useMemo(() => template?.fields ?? [], [template])
  const booleanFields = useMemo(() => fields.filter((field) => field.type === FormFieldType.Boolean), [fields])
  const hasNonBooleanFields = useMemo(() => fields.some((field) => field.type !== FormFieldType.Boolean), [fields])

  const submission = useMemo(
    () => submissionsData?.items?.find((item) => item.purpose === FormSubmissionPurpose.ReadinessChecklist),
    [submissionsData]
  )

  const items = useMemo(
    () =>
      booleanFields.map((field) => ({
        fieldId: field.id,
        label: field.label,
        done: submission?.responses?.find((response) => response.fieldId === field.id)?.value === true,
      })),
    [booleanFields, submission]
  )

  const status: ReadinessChecklistStatus = submission
    ? submission.status === FormSubmissionStatus.Submitted
      ? 'submitted'
      : 'draft'
    : 'not-started'

  // Required-field progress (used for the non-boolean soft-confirm hint).
  const requiredFields = useMemo(() => fields.filter((field) => field.required), [fields])
  const answeredRequired = requiredFields.filter(
    (field) => isAnswered(submission?.responses?.find((response) => response.fieldId === field.id)?.value)
  ).length

  const completed = items.filter((item) => item.done).length
  const total = items.length

  const isComplete = hasNonBooleanFields ? status === 'submitted' : total > 0 && completed === total
  const uncheckedCount = hasNonBooleanFields ? Math.max(0, requiredFields.length - answeredRequired) : total - completed

  const toggleItem = async (fieldId: string, done: boolean) => {
    const patientId = consultation?.patient?._id
    if (!templateId || !consultationId || !patientId) return
    const responses = items.map((item) => ({ fieldId: item.fieldId, value: item.fieldId === fieldId ? done : item.done }))
    try {
      if (submission) {
        await updateFormSubmission({ id: submission._id, changes: { responses } }).unwrap()
      } else {
        await createFormSubmission({
          template: templateId,
          patient: patientId,
          consultation: consultationId,
          purpose: FormSubmissionPurpose.ReadinessChecklist,
          status: FormSubmissionStatus.Draft,
          responses,
        }).unwrap()
      }
    } catch (error) {
      enqueueSnackbar(getApiErrorMessage(error, t('notification.form.checklist.saveError')), { variant: 'error' })
    }
  }

  // Opens the dedicated form-submission page (new tab) scoped to this checklist; on submit the
  // backend flips the submission to 'submitted' and re-broadcasts the room to the doctors.
  const openPreparation = () => {
    const patientId = consultation?.patient?._id
    if (!templateId || !consultationId || !patientId) return
    const base = submission
      ? `/forms/submit/${templateId}/${patientId}/${submission._id}`
      : `/forms/submit/${templateId}/${patientId}`
    window.open(`${base}?type=consultation&objectId=${consultationId}&purpose=${FormSubmissionPurpose.ReadinessChecklist}`, '_blank')
  }

  return {
    enabled: Boolean(templateId && fields.length),
    templateTitle: template?.title ?? '',
    items,
    completed,
    total,
    isSaving: isCreating || isUpdating,
    toggleItem,
    hasNonBooleanFields,
    status,
    isComplete,
    uncheckedCount,
    openPreparation,
  }
}
